import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import StockManager from "@/components/admin/StockManager";
import OrderEnquiries from "@/components/admin/OrderEnquiries";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Search,
  Trash2,
  Mail,
  Phone,
  Clock,
  Inbox,
  Filter,
  LogOut,
  Bell,
  BellOff,
  Printer,
} from "lucide-react";
import {
  getPushStatus,
  subscribeAdminPush,
  unsubscribeAdminPush,
} from "@/lib/push";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard({ onLogout }) {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fabricFilter, setFabricFilter] = useState("all");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [pushStatus, setPushStatus] = useState("default");
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState("");

  const handleLogout = useCallback(async () => {
    try {
      await fetch(`${API}/admin/logout`, { method: "POST", credentials: "include" });
    } catch (err) {
      // Non-fatal — we proceed with local logout regardless.
      console.debug("Admin logout request failed:", err);
    }
    onLogout?.();
    navigate("/admin/login");
  }, [onLogout, navigate]);

  const fetchInquiries = useCallback(async () => {
    try {
      const res = await fetch(`${API}/inquiries`, { credentials: "include" });
      if (res.status === 401) {
        handleLogout();
        return;
      }
      const data = await res.json();
      setInquiries(data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
    } catch (err) {
      console.error("Failed to fetch inquiries:", err);
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    fetchInquiries();
    getPushStatus().then(setPushStatus).catch(() => {});
  }, [fetchInquiries]);

  const togglePush = async () => {
    setPushError("");
    setPushBusy(true);
    try {
      if (pushStatus === "subscribed") {
        await unsubscribeAdminPush();
      } else {
        await subscribeAdminPush();
      }
      setPushStatus(await getPushStatus());
    } catch (err) {
      setPushError(err.message || "Unable to toggle notifications");
    } finally {
      setPushBusy(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API}/inquiries/${id}`, { method: "DELETE", credentials: "include" });
      if (res.status === 401) {
        handleLogout();
        return;
      }
      setInquiries((prev) => prev.filter((i) => i.id !== id));
      setDeleteConfirm(null);
    } catch (err) {
      console.error("Failed to delete:", err);
    }
  };

  const filtered = inquiries.filter((inq) => {
    const matchesSearch =
      !search ||
      inq.name.toLowerCase().includes(search.toLowerCase()) ||
      inq.email.toLowerCase().includes(search.toLowerCase()) ||
      inq.message.toLowerCase().includes(search.toLowerCase());
    const matchesFabric =
      fabricFilter === "all" || inq.fabric_interest === fabricFilter;
    return matchesSearch && matchesFabric;
  });

  const uniqueFabrics = [...new Set(inquiries.map((i) => i.fabric_interest).filter(Boolean))];

  const formatDate = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--mp-paper)" }} data-testid="admin-dashboard">
      {/* Header */}
      <header
        className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[var(--mp-line)]"
        data-testid="admin-header"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16 lg:h-20">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-back-btn">
              <Button variant="ghost" className="p-2 rounded-sm">
                <ArrowLeft className="w-5 h-5" style={{ color: "var(--mp-text-strong)" }} />
              </Button>
            </Link>
            <div>
              <h1
                className="text-xl font-medium"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
              >
                Inquiry Dashboard
              </h1>
              <p className="text-xs" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
                {inquiries.length} total inquiries
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="text-sm font-medium hidden sm:block"
              style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text-strong)" }}
            >
              Mutha Paras & Co.
            </Link>
            {pushStatus !== "unsupported" && pushStatus !== "denied" && (
              <Button
                variant="outline"
                onClick={togglePush}
                disabled={pushBusy}
                className="text-xs px-3 py-2 rounded-sm flex items-center gap-1.5"
                style={{
                  borderColor: pushStatus === "subscribed" ? "#101010" : "#E4DDCE",
                  color: pushStatus === "subscribed" ? "#101010" : "#5C5C5C",
                  fontFamily: "'Manrope', sans-serif",
                }}
                data-testid="admin-push-toggle"
                title={pushError || (pushStatus === "subscribed" ? "Notifications are ON" : "Enable notifications for new inquiries/orders")}
              >
                {pushStatus === "subscribed" ? (
                  <>
                    <Bell className="w-3.5 h-3.5" /> Push ON
                  </>
                ) : (
                  <>
                    <BellOff className="w-3.5 h-3.5" /> Enable Push
                  </>
                )}
              </Button>
            )}
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/products-static/print/rate_card_A4.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs px-3 py-2 rounded-sm border flex items-center gap-1.5"
              style={{ borderColor: "var(--mp-line)", color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
              data-testid="admin-rate-card-btn"
            >
              <Printer className="w-3.5 h-3.5" />
              Rate Card
            </a>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="text-xs px-3 py-2 rounded-sm flex items-center gap-1.5"
              style={{ borderColor: "var(--mp-line)", color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        <OrderEnquiries onUnauthorized={handleLogout} />

        <StockManager />

        {/* Filters */}
        <div
          className="flex flex-col sm:flex-row gap-3 mb-8"
          data-testid="admin-filters"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--mp-muted)" }} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, or message..."
              className="pl-10 rounded-sm border-[var(--mp-line)] bg-white py-5 text-sm"
              style={{ fontFamily: "'Manrope', sans-serif" }}
              data-testid="admin-search-input"
            />
          </div>
          <div className="w-full sm:w-56">
            <Select value={fabricFilter} onValueChange={setFabricFilter}>
              <SelectTrigger
                className="rounded-sm border-[var(--mp-line)] bg-white py-5 text-sm"
                style={{ fontFamily: "'Manrope', sans-serif" }}
                data-testid="admin-fabric-filter"
              >
                <Filter className="w-4 h-4 mr-2" style={{ color: "var(--mp-muted)" }} />
                <SelectValue placeholder="Filter by fabric" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Fabrics</SelectItem>
                {uniqueFabrics.map((f) => (
                  <SelectItem key={f} value={f}>
                    {f}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results count */}
        <p
          className="text-xs font-medium uppercase tracking-wider mb-4"
          style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
        >
          Showing {filtered.length} of {inquiries.length} inquiries
        </p>

        {/* Inquiry list */}
        {loading ? (
          <div className="text-center py-20">
            <p className="text-sm" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
              Loading inquiries...
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 pastel-cream border border-[var(--mp-line)] rounded-sm" data-testid="admin-empty-state">
            <Inbox className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--mp-line)" }} />
            <p
              className="text-lg font-medium mb-1"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            >
              No inquiries found
            </p>
            <p className="text-sm" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
              {search || fabricFilter !== "all"
                ? "Try adjusting your search or filters"
                : "Inquiries from the contact form will appear here"}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((inq, idx) => (
              <div
                key={inq.id}
                className="bg-white border border-[var(--mp-line)] rounded-sm p-5 sm:p-6 transition-all duration-200 hover:shadow-sm"
                data-testid={`inquiry-card-${idx}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  {/* Main content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2">
                      <h3
                        className="text-lg font-medium"
                        style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
                      >
                        {inq.name}
                      </h3>
                      {inq.fabric_interest && (
                        <span
                          className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm pastel-green"
                          style={{ color: "var(--mp-text-strong)", fontFamily: "'Manrope', sans-serif" }}
                        >
                          {inq.fabric_interest}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3">
                      <div className="flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5" style={{ color: "var(--mp-copper)" }} />
                        <a
                          href={`mailto:${inq.email}`}
                          className="text-sm hover:underline"
                          style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
                        >
                          {inq.email}
                        </a>
                      </div>
                      {inq.phone && (
                        <div className="flex items-center gap-1.5">
                          <Phone className="w-3.5 h-3.5" style={{ color: "var(--mp-text-strong)" }} />
                          <a
                            href={`tel:${inq.phone}`}
                            className="text-sm hover:underline"
                            style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
                          >
                            {inq.phone}
                          </a>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" style={{ color: "var(--mp-muted)" }} />
                        <span className="text-xs" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
                          {formatDate(inq.timestamp)}
                        </span>
                      </div>
                    </div>

                    <p
                      className="text-sm leading-relaxed"
                      style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}
                    >
                      {inq.message}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 flex-shrink-0">
                    <a href={`https://wa.me/${inq.phone?.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer">
                      <Button
                        variant="outline"
                        className="text-xs px-3 py-2 rounded-sm"
                        style={{ borderColor: "#25D366", color: "#25D366", fontFamily: "'Manrope', sans-serif" }}
                        data-testid={`inquiry-whatsapp-${idx}`}
                      >
                        Reply
                      </Button>
                    </a>
                    {deleteConfirm === inq.id ? (
                      <div className="flex gap-1">
                        <Button
                          onClick={() => handleDelete(inq.id)}
                          className="text-xs px-3 py-2 rounded-sm text-white"
                          style={{ backgroundColor: "#dc2626", fontFamily: "'Manrope', sans-serif" }}
                          data-testid={`inquiry-confirm-delete-${idx}`}
                        >
                          Confirm
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setDeleteConfirm(null)}
                          className="text-xs px-3 py-2 rounded-sm"
                          style={{ fontFamily: "'Manrope', sans-serif" }}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        onClick={() => setDeleteConfirm(inq.id)}
                        className="p-2 rounded-sm"
                        data-testid={`inquiry-delete-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" style={{ color: "#dc2626" }} />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
